import React, { useEffect, useState } from "react";
import XDate from "xdate";
import useQueryParams from "Utils/useQueryParams";
import WarningBanner from "Components/warningBanner/WarningBanner";
import Checkbox from "Components/checkbox/Checkbox";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import DeliveryDomainStatusBadge from "Components/deliveryDomainStatusBadge/DeliveryDomainStatusBadge";
import t from "Utils/translation";
import QboxmailApi from "@/qboxmailApi";
import Button from "Components/button/Button";
import SmallModal from "Components/smallModal/smallModal";
import { domainsPerDeliveryAccountsLimits } from "@/config";
import Loader from "Components/loader/Loader";
import usePaginatedList from "Utils/usePaginatedList";
import PaginationFilter from "Components/paginationFilter/PaginationFilter";
import { useHistory, useLocation } from "react-router-dom";
import useFormState from "Utils/useFormState";
import { addDomain as addDomainSvg, Icon, close } from "Icons";
import { alertError, alertSuccess } from "Utils/alerts";
import ResponsiveButtonGroup from "Components/responsiveButtonGroup/ResponsiveButtonGroup";
import cx from "classnames";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import s from "../../delivery.scss";

const api = new QboxmailApi({ useCookie: true });

const AddDomainModal = ({
  isOpen = false,
  selectedDomainsCodes = [],
  customerCode = null,
  onButtonCancelClick,
  onButtonAcceptClick,
  managerCode,
  managerFullname,
  accountId,
}) => {
  const queryParams = useQueryParams();
  const location = useLocation();
  const history = useHistory();

  const apiPath = `/delivery/domains?per=999999999999&${
    customerCode ? `customer_code=${customerCode}&` : ""
  }${queryParams.toString()}`;

  const { data, error } = usePaginatedList(apiPath);
  const [selectedDomains, setSelectedDomains] = useState({});
  const [willHaveManagerAlert, setWillHaveManagerAlert] = useState(null);
  // const [previewingManagerResources, setPreviewingManagerResources] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setWillHaveManagerAlert(null);
    }
  }, [isOpen]);

  const searchParams = useQueryParams();

  const accountHasManager = !!managerCode;

  // const preview = async () => {
  //   setPreviewingManagerResources(true);

  //   const codes = Object.values(selectedDomains).map((d) => d.code);
  //   const res = await api.previewManagerResources(accountId, codes);
  //   console.log("res", res);
  //   setPreviewingManagerResources(false);
  //   return true;
  // };

  return (
    <SmallModal
      title={t("delivery.accounts.domainsPage.button.bindDomain")}
      opened={isOpen}
      footerComponent={
        <div className="">
          <Button
            color="outline"
            onClick={() => {
              onButtonCancelClick();
              setSelectedDomains({});
            }}
          >
            button.cancel
          </Button>
          <Button
            disabled={Object.keys(selectedDomains).length === 0}
            color="accent"
            onClick={async () => {
              // const result = await preview();
              // TODO show the result
              // if (result) return;

              onButtonAcceptClick(selectedDomains);
              setSelectedDomains({});
            }}
          >
            button.add
          </Button>
        </div>
      }
    >
      <div>
        <div className="sectionInfo">{t("delivery.accounts.domains.connectedDomains.modalMessage")}</div>
        <div className="mt-8">
          <PaginationFilter currentQuery={searchParams.query} />
          <div className="section">
            <ResponsiveTable
              location={location}
              areRowsLoading={!data || error}
              history={history}
              headers={[
                {
                  name: t("table.header.domain"),
                  colSpan: 2,
                },
                { name: t("table.header.date.creation") },
              ]}
              rows={
                data
                  ? data.resources
                      .filter((domainData) => !selectedDomainsCodes?.includes(domainData.code))
                      .map((domainData) => (
                        <tr key={domainData.code}>
                          <td colSpan={2}>
                            <Checkbox
                              name={domainData.name}
                              value={domainData.code}
                              //eslint-disable-next-line
                              onChange={(e) => {
                                if (selectedDomains[domainData.code]) {
                                  const currentSelection = { ...selectedDomains };
                                  delete currentSelection[domainData.code];

                                  if (willHaveManagerAlert) {
                                    let managerSelected = false;
                                    for (let code in currentSelection) {
                                      managerSelected = currentSelection[code].manager_fullname;
                                      if (managerSelected) {
                                        break;
                                      }
                                    }

                                    if (!managerSelected) setWillHaveManagerAlert(null);
                                  }

                                  setSelectedDomains(currentSelection);
                                  return;
                                }

                                if (!accountHasManager && domainData.manager_fullname) {
                                  setWillHaveManagerAlert(domainData.manager_fullname);
                                }

                                setSelectedDomains({
                                  ...selectedDomains,
                                  [domainData.code]: {
                                    code: domainData.code,
                                    name: domainData.name,
                                    status: domainData.status,
                                    status_detail: domainData.status_detail,
                                    approval_status: domainData.approval_status,
                                    dns_verification_status: domainData.dns_verification_status,
                                  },
                                });
                              }}
                              radioStyle
                              checked={selectedDomains[domainData.code]}
                              valueOnly
                              disabled={
                                (accountHasManager &&
                                  domainData.manager_code &&
                                  managerCode !== domainData.manager_code) ||
                                // has already selected a domain with another manager. Resources can have only one manager.
                                (willHaveManagerAlert &&
                                  domainData.manager_fullname &&
                                  willHaveManagerAlert !== domainData.manager_fullname)
                              }
                              hint={
                                domainData.manager_code && managerCode !== domainData.manager_code
                                  ? `Assegnato a manager (${domainData.manager_fullname})`
                                  : domainData.accounts_count > 0
                                  ? `Utenze associate: ${domainData.accounts_count}`
                                  : null
                              }
                            />
                          </td>
                          <td>{new XDate(domainData.created_at).toString("dd/MM/yyyy")}</td>
                        </tr>
                      ))
                  : []
              }
              addIcon={addDomainSvg}
              addMsg={t("delivery.accounts.domains.noExistingDomain.msg")}
              addDesc={t("delivery.accounts.domains.noExistingDomain.desc")}
            />

            {managerFullname && (
              <div className="w-full my-4">
                <WarningBanner
                  color="yellow"
                  msg={`I domini associati e le loro utenze saranno gestibili dal manager dell'account:  <b>${managerFullname}</b>`}
                />
              </div>
            )}

            {willHaveManagerAlert && (
              <div className="w-full my-4">
                <WarningBanner
                  color="yellow"
                  msg={`Questa utenza e tutti i domini associati diventeranno gestibili dal manager:  <b>${willHaveManagerAlert}</b>`}
                />
              </div>
            )}
          </div>
        </div>
        {/* <div className="section sectionDesc">
          {t("delivery.accounts.domains.connectedDomains.modalLabel")}: {selectedDomain && selectedDomain.name}
        </div> */}
      </div>
    </SmallModal>
  );
};

const Domains = ({
  accountId,
  accountData,
  domains,
  customerCode,
  isAdding = false,
  onDomainsUploaded = null,
  loggedUser,
}) => {
  const location = useLocation();
  const history = useHistory();
  const {
    formData,
    setFormData,
    isFormLoading,
    setFormLoading,
    unsetFormLoading,
    hasChanged,
    resetToInitialFormState,
    setInitialValues,
  } = useFormState({ selectedDomains: domains });

  const [domainsError, setDomainsError] = useState(false);
  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  useEffect(() => {
    setInitialValues({ selectedDomains: domains });
  }, [domains, setInitialValues]);

  const navigateFromModalToDetails = () => {
    history.push(location.pathname.split("/").slice(0, -1).join("/"));
  };

  const removeDomain = (code) => {
    setFormData((prev) => ({ selectedDomains: prev.selectedDomains.filter((v) => v.code !== code) }));
  };

  const addDomains = (domains) => {
    setFormData((prev) => {
      return { selectedDomains: [...(prev.selectedDomains || []), ...Object.values(domains)] };
    });

    navigateFromModalToDetails();
  };

  const updateAccountDomains = async () => {
    setFormLoading();
    setDomainsError(false);

    try {
      const resp = await api.updateDeliveryAccountInfo({
        id: accountId,
        domainCodes: formData.selectedDomains.map((v) => v.code),
      });
      if (resp) {
        onDomainsUploaded && onDomainsUploaded();
      }

      alertSuccess(t("alert.deliveryAccount.updated"));
    } catch (error) {
      const errors = await error.json();

      setDomainsError(true);

      console.log(errors);

      console.error(error);
      alertError(t("alert.wrong"));
    }
    unsetFormLoading();
  };

  const canEditDomainsAssociation = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("edit_delivery_account_domains_association")
  );

  return (
    <div className={cx(s.dashboardContainer, s.noMarginTop)}>
      <div className={s.dashboardHeader}>
        <ResponsiveButtonGroup
          className="flex items-center justify-end expand gap20"
          buttonElements={[
            hasChanged ? (
              <Button onClick={resetToInitialFormState} color="outline" key={0}>
                button.cancel
              </Button>
            ) : null,
            <Button loading={isFormLoading} onClick={updateAccountDomains} key={1}>
              button.save
            </Button>,
          ]}
          warningElement={hasChanged && <WarningBanner color="yellow-text" msg={t("infoMsg.unsaveChanged")} />}
        />
      </div>
      <div className={s.dashboardContent}>
        <div className={s.dashboardItem1}>
          <div className="settingTitle">{t("delivery.accounts.domains.connectedDomains.title")}</div>
          <div className="sectionDesc">{t("delivery.accounts.domains.connectedDomains.desc")}</div>
        </div>
        <div className={s.dashboardItem2}>
          {formData.selectedDomains && (
            <ResponsiveTable
              location={location}
              headers={[
                { name: t("table.header.status"), size: "1rem" },
                {
                  name: t("table.header.domain"),
                  size: "10rem",
                },
                { name: t("table.header.status") },
                { size: "3rem", center: true },
              ]}
              rows={
                formData.selectedDomains
                  ? formData.selectedDomains.map((domain) => (
                      <tr key={domain.code}>
                        <td>
                          <div className="expand flexCenter">
                            <DeliveryDomainStatusBadge domain={domain} />
                          </div>
                        </td>
                        <td>{domain.name}</td>
                        <td>
                          <DeliveryDomainStatusBadge domain={domain} textOnly />
                        </td>
                        <td>
                          <div style={{ cursor: "pointer" }} onClick={() => removeDomain(domain.code)}>
                            <Icon svg={close} />
                          </div>
                        </td>
                      </tr>
                    ))
                  : []
              }
              showAddIcon={false}
              showAddMsg={false}
              addDesc={t("delivery.accounts.domains.noAssociatedDomain.desc")}
            />
          )}

          {domainsError && (
            <div className="flex justify-center w-full pt-6">
              <WarningBanner
                msg={t("delivery.accounts.domains.connectedDomains.exceededMsg", null, {
                  current: formData.selectedDomains?.length || 0,
                })}
                color="red"
              />
            </div>
          )}

          <div className="flex flex-col items-end justify-between gap-4 mt-6">
            <WarningBanner
              msg={t("delivery.accounts.domains.connectedDomains.warningMsg", null, {
                domains: domainsPerDeliveryAccountsLimits[accountData.plan === "free" ? "free" : "paid"],
              })}
              fullWidth
              color="blue"
              info
            />

            <Button
              onClick={() => {
                if (!canEditDomainsAssociation) {
                  setIsForbiddenModalOpen(true);
                  return;
                }

                history.push(`${location.pathname}/add`);
              }}
              color="outline"
            >
              delivery.accounts.domainsPage.button.bindDomain
            </Button>
          </div>
        </div>
      </div>
      <AddDomainModal
        isOpen={isAdding}
        customerCode={customerCode}
        managerCode={accountData.manager_code}
        managerFullname={accountData.manager_fullname}
        onButtonCancelClick={navigateFromModalToDetails}
        onButtonAcceptClick={addDomains}
        selectedDomainsCodes={formData.selectedDomains && formData.selectedDomains.map((v) => v.code)}
        accountId={accountData.id}
      />

      <ConfirmModal
        title={t("teamMembers.forbiddenAction")}
        text={t("teamMembers.forbiddenActionSubtitle")}
        textButton="button.ok"
        hideCancelButton={true}
        opened={isForbiddenModalOpen}
        action={() => setIsForbiddenModalOpen(false)}
        handleClose={() => setIsForbiddenModalOpen(false)}
        buttonColor="accent"
      />
    </div>
  );
};

export default Domains;
